import { Link } from "react-router-dom";
import {
  ArrowRight,
  Grid3x3,
  Layers,
  Flame,
  Compass,
  Lightbulb,
  SlidersHorizontal,
} from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";

const ICONS = {
  Grid3x3,
  Layers,
  Flame,
  Compass,
  Lightbulb,
  SlidersHorizontal,
};

export default function Standards() {
  const { content } = useContent();
  const standards = content.standards || [];
  const quote = content.standardQuote || "";
  const headRef = useReveal();
  const quoteRef = useReveal();
  return (
    <section data-testid="standards-section" className="bg-[#f9f8f6]">
      {/* Antraštė */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-[140px] md:pt-[180px] pb-12 md:pb-24">
        <div
          className="reveal grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
          ref={headRef}
        >
          <div className="md:col-span-7">
            <span className="overline">— ManoPirtis standartas</span>
            <h1 className="mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#1a1a1a]">
              Kokybės ir
              <br />
              <span className="italic">architektūros standartas.</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-md md:ml-auto">
              Sprendimai, kurie nematyti iš pirmo žvilgsnio, bet apibrėžia,
              kaip pirtis tarnaus dešimt ar dvidešimt metų. Be šūkių – tik
              detalės, kurios yra svarbios.
            </p>
          </div>
        </div>
      </div>

      {/* Standartai */}
      <div className="border-t border-[#e5e2dd]">
        {standards.map((s, i) => (
          <StandardItem
            key={s.id}
            item={s}
            index={i}
            last={i === standards.length - 1}
          />
        ))}
      </div>

      {/* Citata */}
      <div
        ref={quoteRef}
        data-testid="standards-quote"
        className="reveal bg-[#1a1a1a] text-[#f9f8f6] py-28 md:py-44"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center gap-4 mb-12">
            <span className="w-10 h-px bg-[#a66e4b]" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#f9f8f6]/70">
              Iš autoriaus
            </span>
          </div>
          <blockquote className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-[64px] leading-[1.12] tracking-tight font-light max-w-[20ch]">
            <span className="text-[#a66e4b] mr-3 align-top">„</span>
            <span className="italic">{quote}</span>
            <span className="text-[#a66e4b] ml-2 align-top">“</span>
          </blockquote>
          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/kontaktai" data-testid="standards-cta-contact" className="btn-light">
              Pakalbėkime apie jūsų projektą{" "}
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/projektai" data-testid="standards-cta-projects" className="btn-ghost-light">
              Peržiūrėti projektus
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function StandardItem({ item, index, last }) {
  const ref = useReveal();
  const Icon = ICONS[item.icon];
  const num = String(index + 1).padStart(2, "0");
  return (
    <article
      id={`standartas-${item.id}`}
      data-testid={`standard-${item.id}`}
      className={`border-b border-[#e5e2dd] ${last ? "" : ""}`}
    >
      <div
        ref={ref}
        className="reveal max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
        style={{ transitionDelay: `${(index % 3) * 80}ms` }}
      >
        {/* Left rail: number + icon */}
        <div className="md:col-span-4">
          <div className="flex items-start gap-6">
            <div className="font-serif-display italic text-[#a66e4b] text-5xl md:text-6xl leading-none font-light">
              {num}
            </div>
            {Icon && (
              <div className="mt-2 w-14 h-14 border border-[#e5e2dd] flex items-center justify-center bg-[#f9f8f6]">
                <Icon size={22} strokeWidth={1.25} className="text-[#1a1a1a]" />
              </div>
            )}
          </div>
          <span className="overline mt-8 block">— Standartas {num}</span>
        </div>

        {/* Right body */}
        <div className="md:col-span-8">
          <h2 className="font-serif-display text-2xl md:text-3xl lg:text-[36px] leading-tight tracking-tight text-[#1a1a1a] max-w-2xl">
            {item.title}
          </h2>
          <div className="mt-6 md:mt-8 space-y-5 text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-2xl">
            {item.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {item.bullets && (
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl">
              {item.bullets.map((b) => (
                <li
                  key={b}
                  className="flex gap-3 text-[15px] text-[#3a3a3a] font-light leading-relaxed"
                >
                  <span className="mt-[10px] inline-block w-3 h-px bg-[#a66e4b] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {item.footer && (
            <p className="mt-8 pt-6 border-t border-[#e5e2dd] text-[15px] text-[#1a1a1a] font-light italic leading-relaxed max-w-2xl">
              {item.footer}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
